import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FiGrid, FiList } from "react-icons/fi";
import kitAxiosInstance from "../lib/kitAxios";
import { useUsersByIds } from "../hooks/useUsers";

const ticketColumns = [
  { key: "new", label: "New", dot: "bg-slate-500" },
  { key: "triaged", label: "Triaged", dot: "bg-sky-500" },
  { key: "in-progress", label: "In progress", dot: "bg-amber-500" },
  { key: "waiting", label: "Waiting", dot: "bg-orange-300" },
  { key: "resolved", label: "Resolved", dot: "bg-emerald-600" },
  { key: "closed", label: "Closed", dot: "bg-red-600" },
];

const ticketTypes = ["All", "Bug", "Feature", "Question", "Incident"];
const productFilters = [
  { label: "All products", value: "all" },
  { label: "AizoPlan", value: "AizoPlan" },
  { label: "AizoAdviser", value: "AizoAdviser" },
];

const statusDots = {
  new: "bg-slate-500",
  triaged: "bg-sky-500",
  "in-progress": "bg-amber-500",
  waiting: "bg-orange-300",
  resolved: "bg-emerald-600",
  closed: "bg-red-600",
};

const typeStyles = {
  Bug: {
    badge: "bg-rose-50 text-rose-500",
    marker: "bg-rose-500",
  },
  Feature: {
    badge: "bg-violet-50 text-violet-500",
    marker: "bg-violet-500",
  },
  Question: {
    badge: "bg-sky-50 text-sky-500",
    marker: "bg-sky-500",
  },
  Incident: {
    badge: "bg-orange-50 text-orange-500",
    marker: "bg-orange-500",
  },
  Uncategorized: {
    badge: "bg-slate-100 text-slate-500",
    marker: "bg-slate-400",
  },
};

const projectStyles = {
  AizoPlan: "bg-indigo-50 text-indigo-500",
  AizoAdviser: "bg-cyan-50 text-cyan-600",
};

function TicketCard({ ticket, onDragStart, onDragEnd, onOpen }) {
  const typeStyle = typeStyles[ticket.type] || typeStyles.Uncategorized;

  return (
    <article
      draggable
      onClick={() => onOpen(ticket)}
      onDragStart={(event) => onDragStart(event, ticket.id)}
      onDragEnd={onDragEnd}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") onOpen(ticket);
      }}
      role="button"
      tabIndex={0}
      className="cursor-grab rounded-lg border border-slate-200 bg-white p-2.5 shadow-sm active:cursor-grabbing"
    >
      <div className="mb-1.5 flex items-center justify-between gap-2">
        <span className="text-[10px] font-medium text-slate-400">{ticket.id}</span>
        <span className={`h-1.5 w-1.5 rounded-full ${statusDots[ticket.status]}`} aria-hidden="true" />
      </div>
      <h3 className="min-h-[2.5rem] text-[11px] font-medium leading-4 text-slate-700">
        {ticket.title}
      </h3>
      <div className="mt-2 flex items-center gap-1.5 text-[9px] font-medium">
        <span className={`inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 ${typeStyle.badge}`}>
          <span className={`h-1.5 w-1.5 rounded-full ${typeStyle.marker}`} aria-hidden="true" />
          {ticket.type}
        </span>
        <span className={`inline-flex items-center rounded-full px-1.5 py-0.5 ${projectStyles[ticket.project]}`}>
          {ticket.project}
        </span>
      </div>
      <div className="mt-2 flex items-center justify-between gap-2 text-[9px] text-slate-400">
        <span className="truncate">{ticket.client}</span>
        <span className="shrink-0">{ticket.organization}</span>
      </div>
    </article>
  );
}

function TicketList({ tickets, onStatusChange, onOpen }) {
  return (
    <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white shadow-sm">
      <table className="min-w-full text-left text-xs">
        <thead className="border-b border-slate-200 bg-slate-50 text-[10px] uppercase tracking-wide text-slate-400">
          <tr>
            <th className="px-3 py-2.5 font-medium">ID</th>
            <th className="px-3 py-2.5 font-medium">Title</th>
            <th className="px-3 py-2.5 font-medium">Client</th>
            <th className="px-3 py-2.5 font-medium">Organization</th>
            <th className="px-3 py-2.5 font-medium">Type</th>
            <th className="px-3 py-2.5 font-medium">Product</th>
            <th className="px-3 py-2.5 font-medium">Status</th>
            <th className="px-3 py-2.5 font-medium">Created</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100 text-slate-600">
          {tickets.map((ticket) => {
            const typeStyle = typeStyles[ticket.type] || typeStyles.Uncategorized;
            const projectStyle = projectStyles[ticket.project] || "bg-slate-100 text-slate-500";
            const statusLabel = ticket.status.replace("-", " ");

            return (
              <tr
                key={ticket.id}
                onClick={() => onOpen(ticket)}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") onOpen(ticket);
                }}
                role="button"
                tabIndex={0}
                className="cursor-pointer hover:bg-slate-50"
              >
                <td className="whitespace-nowrap px-3 py-3 text-[10px] text-slate-400">{ticket.id}</td>
                <td className="min-w-56 px-3 py-3 text-[11px] font-medium text-slate-700">{ticket.title}</td>
                <td className="whitespace-nowrap px-3 py-3 text-[10px]">{ticket.client}</td>
                <td className="whitespace-nowrap px-3 py-3 text-[10px]">{ticket.organization || "-"}</td>
                <td className="whitespace-nowrap px-3 py-3">
                  <span className={`inline-flex rounded-full px-2 py-1 text-[9px] ${typeStyle.badge}`}>
                    {ticket.type}
                  </span>
                </td>
                <td className="whitespace-nowrap px-3 py-3">
                  <span className={`inline-flex rounded-full px-2 py-1 text-[9px] ${projectStyle}`}>
                    {ticket.project}
                  </span>
                </td>
                <td className="whitespace-nowrap px-3 py-3">
                  <div className="flex items-center gap-2">
                    <div className="w-full inline-flex items-center gap-1.5 text-[10px] capitalize">
                      <span className={`h-1.5 w-1.5 rounded-full ${statusDots[ticket.status] || "bg-slate-400"}`} />
                      {statusLabel}
                    </div>
                    <select
                      value={ticket.status}
                      onChange={(event) => onStatusChange(ticket, event.target.value)}
                      onClick={(event) => event.stopPropagation()}
                      aria-label={`Change status for ${ticket.id}`}
                      className="w-full rounded border border-slate-200 bg-white px-1.5 py-1 text-[9px] text-slate-500 outline-none focus:border-slate-400"
                    >
                      {ticketColumns.map((status) => (
                        <option key={status.key} value={status.key}>{status.label}</option>
                      ))}
                    </select>
                  </div>
                </td>
                <td className="whitespace-nowrap px-3 py-3 text-[10px] text-slate-400">
                  {ticket.createdAt ? new Date(ticket.createdAt).toLocaleDateString() : "-"}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function Tickets() {
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [selectedType, setSelectedType] = useState("All");
  const [selectedProduct, setSelectedProduct] = useState("all");
  const [viewMode, setViewMode] = useState("board");
  const [selectedStatus, setSelectedStatus] = useState("all");
  const [ticketItems, setTicketItems] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");
  const [draggedTicketId, setDraggedTicketId] = useState(null);
  const [dragOverColumn, setDragOverColumn] = useState(null);

  useEffect(() => {
    const fetchTickets = async () => {
      try {
        const { data } = await kitAxiosInstance.get("/tickets", {
          params: { page: 1, limit: 100 },
        });

        setTicketItems(
          (data.tickets || []).map((ticket) => ({
            ...ticket,
            backendId: ticket.id,
            id: `AZ-${String(ticket.id).padStart(4, "0")}`,
            type: ticket.category
              ? ticket.category.charAt(0).toUpperCase() + ticket.category.slice(1)
              : "Uncategorized",
            project: ticket.type === "aizoadviser" ? "AizoAdviser" : "AizoPlan",
          }))
        );
      } catch (requestError) {
        setError(requestError?.response?.data?.message || "Unable to load tickets.");
      } finally {
        setIsLoading(false);
      }
    };

    fetchTickets();
  }, []);

  const usersById = useUsersByIds(ticketItems.map((ticket) => ticket.userId));

  const ticketsWithClient = useMemo(
    () =>
      ticketItems.map((ticket) => {
        const user = usersById.get(String(ticket.userId));
        const name = [user?.firstName, user?.lastName].filter(Boolean).join(" ");
        return {
          ...ticket,
          client: name || user?.email || `User #${ticket.userId}`,
          organization: user?.organization?.name || "",
        };
      }),
    [ticketItems, usersById]
  );

  const filteredTickets = useMemo(() => {
    const query = search.trim().toLowerCase();

    const filtered = ticketsWithClient.filter((ticket) => {
      const matchesType = selectedType === "All" || ticket.type === selectedType;
      const matchesProduct = selectedProduct === "all" || ticket.project === selectedProduct;
      const matchesStatus = viewMode !== "list" || selectedStatus === "all" || ticket.status === selectedStatus;
      const matchesSearch =
        !query ||
        ticket.id.toLowerCase().includes(query) ||
        ticket.title.toLowerCase().includes(query) ||
        ticket.client.toLowerCase().includes(query);

      return matchesType && matchesProduct && matchesStatus && matchesSearch;
    });

    return [...filtered].sort((first, second) => {
      return new Date(second.createdAt || 0).getTime() - new Date(first.createdAt || 0).getTime();
    });
  }, [search, selectedProduct, selectedStatus, selectedType, ticketsWithClient, viewMode]);

  const handleDragStart = (event, ticketId) => {
    event.dataTransfer.effectAllowed = "move";
    event.dataTransfer.setData("text/plain", ticketId);
    setDraggedTicketId(ticketId);
  };

  const handleDragEnd = () => {
    setDraggedTicketId(null);
    setDragOverColumn(null);
  };

  const handleStatusChange = (ticket, status) => {
    if (!ticket || ticket.status === status) return;

    setTicketItems((currentTickets) =>
      currentTickets.map((currentTicket) =>
        currentTicket.id === ticket.id ? { ...currentTicket, status } : currentTicket
      )
    );

    kitAxiosInstance.patch(`/tickets/${ticket.backendId}`, { status }).catch((requestError) => {
      setTicketItems((currentTickets) =>
        currentTickets.map((currentTicket) =>
          currentTicket.id === ticket.id
            ? { ...currentTicket, status: ticket.status }
            : currentTicket
        )
      );
      setError(requestError?.response?.data?.message || "Unable to update ticket status.");
    });
  };

  const handleDrop = (event, status) => {
    event.preventDefault();
    const ticketId = event.dataTransfer.getData("text/plain") || draggedTicketId;

    if (!ticketId) return;

    const ticket = ticketItems.find((item) => item.id === ticketId);

    handleStatusChange(ticket, status);

    handleDragEnd();
  };

  const handleOpenTicket = (ticket) => {
    navigate(`/tickets/${ticket.backendId}`);
  };

  return (
    <>
      <div className="mb-4 flex items-start justify-between gap-4">
        <div>
        <h1 className="text-base font-medium text-slate-700">Tickets</h1>
        <p className="mt-0.5 text-xs text-slate-400">Triage and resolve incoming tickets.</p>
        </div>
        <div className="flex flex-col items-end gap-1" role="group" aria-label="Ticket view and sorting controls">
          <div className="flex items-center rounded-md border border-slate-200 bg-white p-0.5" role="group" aria-label="Choose ticket view">
            <button
              type="button"
              onClick={() => {
                setViewMode("board");
                setSelectedStatus("all");
              }}
              aria-label="Board view"
              title="Board view"
              className={`rounded p-1.5 transition-colors ${viewMode === "board" ? "bg-slate-800 text-white" : "text-slate-500 hover:text-slate-700"}`}
            >
              <FiGrid aria-hidden="true" />
            </button>
            <button
              type="button"
              onClick={() => setViewMode("list")}
              aria-label="List view"
              title="List view"
              className={`rounded p-1.5 transition-colors ${viewMode === "list" ? "bg-slate-800 text-white" : "text-slate-500 hover:text-slate-700"}`}
            >
              <FiList aria-hidden="true" />
            </button>
          </div>
          {viewMode === "list" && (
            <select
              value={selectedStatus}
              onChange={(event) => setSelectedStatus(event.target.value)}
              aria-label="Filter tickets by status"
              className="rounded border border-slate-200 bg-white px-2 py-1 text-[9px] text-slate-500 outline-none focus:border-slate-400"
            >
              <option value="all">Filter: All statuses</option>
              {ticketColumns.map((status) => (
                <option key={status.key} value={status.key}>Filter: {status.label}</option>
              ))}
            </select>
          )}
        </div>
      </div>

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <label className="relative">
          <span className="sr-only">Search tickets or clients</span>
          <svg className="pointer-events-none absolute left-2 top-1/2 h-3 w-3 -translate-y-1/2 text-slate-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="11" cy="11" r="7" />
            <path d="m20 20-4-4" />
          </svg>
          <input
            type="search"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search tickets or clients..."
            className="h-7 w-52 rounded-md border border-slate-200 bg-white pl-7 pr-2 text-[10px] text-slate-600 placeholder:text-slate-400 focus:border-slate-300 focus:outline-none focus:ring-1 focus:ring-slate-200"
          />
        </label>

        <div className="flex items-center gap-1.5" role="group" aria-label="Filter tickets by type">
          {ticketTypes.map((type) => (
            <button
              key={type}
              type="button"
              onClick={() => setSelectedType(type)}
              className={`h-7 rounded-full border px-2.5 text-[9px] transition-colors ${
                selectedType === type
                  ? "border-slate-800 bg-slate-800 text-white"
                  : "border-slate-200 bg-white text-slate-500 hover:border-slate-300 hover:text-slate-700"
              }`}
            >
              {type}
            </button>
          ))}
        </div>

        <div className="flex items-center gap-1.5" role="group" aria-label="Filter tickets by product">
          {productFilters.map((product) => (
            <button
              key={product.value}
              type="button"
              onClick={() => setSelectedProduct(product.value)}
              className={`h-7 rounded-full border px-2.5 text-[9px] transition-colors ${
                selectedProduct === product.value
                  ? "border-slate-800 bg-slate-800 text-white"
                  : "border-slate-200 bg-white text-slate-500 hover:border-slate-300 hover:text-slate-700"
              }`}
            >
              {product.label}
            </button>
          ))}
        </div>
      </div>

      {isLoading && <p className="text-xs text-slate-400">Loading tickets...</p>}
      {error && <p className="text-xs text-rose-500">{error}</p>}

      {!isLoading && !error && viewMode === "list" && <TicketList tickets={filteredTickets} onStatusChange={handleStatusChange} onOpen={handleOpenTicket} />}

      {!isLoading && !error && viewMode === "board" && (
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-6">
        {ticketColumns.map((column) => {
          const columnTickets = filteredTickets.filter((ticket) => ticket.status === column.key);

          return (
            <section
              key={column.key}
              onDragOver={(event) => {
                event.preventDefault();
                event.dataTransfer.dropEffect = "move";
                setDragOverColumn(column.key);
              }}
              onDrop={(event) => handleDrop(event, column.key)}
              className={`min-h-[18rem] rounded-lg p-1.5 transition-colors ${
                dragOverColumn === column.key ? "bg-slate-200" : "bg-slate-100/70"
              }`}
            >
              <header className="mb-2 flex items-center justify-between px-1 text-[10px]">
                <div className="flex items-center gap-1.5">
                  <span className={`h-1.5 w-1.5 rounded-full ${column.dot}`} aria-hidden="true" />
                  <h2 className="font-medium text-slate-500">{column.label}</h2>
                </div>
                <span className="text-slate-400">{columnTickets.length}</span>
              </header>
              <div className="space-y-2">
                {columnTickets.map((ticket) => (
                  <TicketCard
                    key={ticket.id}
                    ticket={ticket}
                    onDragStart={handleDragStart}
                    onDragEnd={handleDragEnd}
                    onOpen={handleOpenTicket}
                  />
                ))}
              </div>
            </section>
          );
        })}
      </div>
      )}
    </>
  );
}

export default Tickets;
